/**
 * Promise concurrency limiter (editable-plan spec 0.7, run-wide MatterGen pool).
 *
 * createLimiter(n) returns limit(fn). fn starts as soon as fewer than n tasks are in
 * flight; otherwise it waits in FIFO order. limit(fn) resolves or rejects with fn's
 * result, and a failing task frees its slot exactly like a succeeding one.
 *
 * useToolsRunner's runRows creates ONE limiter per run and hands it to every MatterGen
 * row, so the bound holds across the whole run, not per row.
 *
 * An invalid n (missing, NaN, below 1) behaves as 1; a fractional n is floored.
 */
export function createLimiter(n) {
  const parsed = Math.floor(Number(n));
  const concurrency = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  let active = 0;
  const queue = [];

  const startNext = () => {
    if (active >= concurrency || queue.length === 0) return;
    const { fn, resolve, reject } = queue.shift();
    active += 1;
    let result;
    try {
      result = Promise.resolve(fn());
    } catch (err) {
      result = Promise.reject(err);
    }
    result.then(resolve, reject).finally(() => {
      active -= 1;
      startNext();
    });
  };

  const limit = (fn) => new Promise((resolve, reject) => {
    queue.push({ fn, resolve, reject });
    startNext();
  });

  Object.defineProperties(limit, {
    concurrency: { value: concurrency, enumerable: true },
    activeCount: { get: () => active, enumerable: true },
    pendingCount: { get: () => queue.length, enumerable: true },
  });
  return limit;
}

export default createLimiter;
